import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';

// components
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { ServerVarsContextConsumer } from '../global/ServerVarsContext/ServerVarsContext';
import { PdpHead } from './PdpHead';
import { SharedTrackingContainer } from '../shared/SharedTrackingContainer';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';
import { BrowserHistoryContextProvider } from '../global/BrowserHistoryContext/BrowserHistoryContext';
import { getSkuTrackingParams } from './tracking/helpers/getSkuTrackingParams';

// helpers
import {
    getUserZipCode,
    getUserSessionCountryCode,
} from 'dibs-buyer-layout/exports/regionalInfoHelpers';
import get from 'lodash.get';
import { trackEcommerce } from './tracking/pdpTracking';
import { getIsFreeShipping } from 'dibs-search-product-tile/src/helpers/freeShippingHelpers';

export class PdpSharedLayoutComponent extends React.Component {
    constructor(props) {
        super(props);

        this.getFollowSearchData = this.getFollowSearchData.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.trackingFunction = this.trackingFunction.bind(this);
        this.onSkuChange = this.onSkuChange.bind(this);
        this.state = { skuId: undefined };
    }

    getFollowSearchData() {
        const searchPage = get(this.props, 'viewer.itemRead.0.saveSearchData.page');
        return { followSearchPages: [searchPage], fetchFollowSearch: !!searchPage };
    }

    isMultiSku() {
        return !!get(this.props, 'viewer.itemRead.0.isMultiSku');
    }

    handleUserRefetch(variables, renderVariables, callback) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const fetchPdp = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;

        const refetchVariables = {
            ...variables,
            ...this.getFollowSearchData(),
            fetchPdp,
            isMultiSku: this.isMultiSku(),
            userZipCode: getUserZipCode(),
            userCountryCode: getUserSessionCountryCode(),
            fetchRegionalInfo: true,
        };
        const updatedRenderVariables = {
            ...refetchVariables,
            ...renderVariables,
            fetchPdp: true,
        };
        this.props.doRefetch(refetchVariables, updatedRenderVariables, err => {
            callback(err, updatedRenderVariables);
        });
    }

    getTrackingData() {
        const params = get(this.props, 'viewer.itemRead.0.pdpTrackingVpvParams') || {};
        const { additionalTrackingData } = this.props;
        const skuParams = this.isMultiSku()
            ? {
                  skuType: 'multi',
                  skuId: this.state.skuId,
                  numberOfSkuImages: this.state.numberOfSkuImages,
                  ...getSkuTrackingParams(params, this.state.skuId),
              }
            : { skuType: 'single' };

        return {
            ...params,
            ...additionalTrackingData,
            ...skuParams,
            hasViewInRoom: get(this.props, 'viewer.itemRead.0.viewInRoomPhotos', []).length,
        };
    }

    trackingFunction() {
        const item = get(this.props, 'viewer.itemRead.0');
        const userRegions = get(this.props, 'viewer.regionalInfo[0].regionsByZipCode') || [];
        const isFreeShipping = getIsFreeShipping({ item, userRegions });
        trackEcommerce(item, this.state.skuId, isFreeShipping);
    }

    onSkuChange(skuId) {
        const skus = get(this.props, 'viewer.itemRead.0.skus');
        const skuImages = skus.find(val => val.skuId === skuId);
        const numberOfSkuImages = skuImages.images.length;

        if (skuId !== this.state.skuId) {
            this.setState({
                skuId,
                numberOfSkuImages,
            });
        }
    }

    render() {
        const { user } = this.props;
        const { url: opt_pageURL, ...additional } = this.getTrackingData() || {}; // eslint-disable-line camelcase
        const gtmPageInfo = { opt_pageURL }; // eslint-disable-line camelcase
        const { fetchFollowSearch } = this.getFollowSearchData();
        const onSkuChange = this.onSkuChange;

        return (
            <BrowserHistoryContextProvider>
                <ServerVarsContextConsumer>
                    {({ clientConf }) => {
                        const pdpHead = new PdpHead({ ...this.props, clientConf });
                        return (
                            <React.Fragment>
                                <SharedUserAndRelayVarLoader
                                    onUserRefetch={this.handleUserRefetch}
                                />
                                <CurrencyProvider
                                    render={({ currency, showCurrency }) => {
                                        return (
                                            <React.Fragment>
                                                <SharedTrackingContainer
                                                    currency={currency}
                                                    user={user}
                                                    gtmPageInfo={gtmPageInfo}
                                                    onTrackingFired={this.trackingFunction}
                                                    additional={additional}
                                                    skuId={this.state.skuId}
                                                />
                                                {this.props.children({
                                                    pdpHead,
                                                    currency,
                                                    showCurrency,
                                                    fetchFollowSearch,
                                                    onSkuChange,
                                                })}
                                            </React.Fragment>
                                        );
                                    }}
                                />
                            </React.Fragment>
                        );
                    }}
                </ServerVarsContextConsumer>
            </BrowserHistoryContextProvider>
        );
    }
}

PdpSharedLayoutComponent.defaultProps = {
    additionalTrackingData: {},
};

PdpSharedLayoutComponent.propTypes = {
    children: PropTypes.func.isRequired,
    additionalTrackingData: PropTypes.object,
    doRefetch: PropTypes.func.isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    intl: intlShape.isRequired,
};

function mapStateToProps({ intlVariables }) {
    return { intlVariables };
}

export const PdpSharedLayout = injectIntl(
    createFragmentContainer(connect(mapStateToProps)(PdpSharedLayoutComponent), {
        viewer: graphql`
            fragment PdpSharedLayout_viewer on Viewer {
                ...PdpHead_viewer @relay(mask: false)
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        isMultiSku
                        pdpTrackingVpvParams(userId: $userId) @include(if: $isClient)
                        saveSearchData {
                            page
                        }
                        ... @include(if: $fetchPdp) {
                            ...PdpHead_item @relay(mask: false)
                            pdpMeta {
                                title
                            }
                            attributes {
                                period {
                                    option {
                                        name
                                    }
                                }
                            }
                            browseUrl
                            categoryCode
                            seller {
                                serviceId
                                isInternal
                            }
                            viewInRoomPhotos {
                                src
                            }
                            contemporaryTrackingString
                        }
                        skus {
                            skuId
                            images {
                                id
                            }
                        }
                        shipmentQuotes {
                            totalAmount {
                                amount
                            }
                            serviceMethod {
                                region {
                                    regionName
                                }
                            }
                        }
                    }
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpSharedLayout_user on User {
                ...SharedTrackingContainer_user
            }
        `,
    })
);
